/**
 * Renvoie un lien vers le configurateur present sur la meme page.
 *
 * Le CTA du hero annonce « composer mon tabouret » : sur l'accueil, ou le
 * configurateur est deux sections plus bas, l'envoyer vers une autre page ferait
 * quitter la landing pour y revenir. On le rabat donc sur la section du
 * configurateur — mais seulement quand il y en a un a portee.
 *
 * Deux partis pris :
 *
 *  1. le `href` est REECRIT, le clic n'est pas detourne. La destination annoncee
 *     reste donc juste (survol, clic milieu, menu contextuel, lecteur d'ecran),
 *     ce qu'un `preventDefault` sur un lien pointant ailleurs ne permet pas ;
 *  2. sans configurateur sur la page — ou sans JS — le lien garde le reglage
 *     `button_link` de la section. Rien ne se casse, on retombe simplement sur
 *     la navigation d'origine.
 *
 * La cible est la SECTION qui contient le configurateur, pas le configurateur
 * lui-meme : on veut arriver sur son en-tete (« Deux choix, un tabouret. »), pas
 * au milieu de l'etape 1.
 */
class GoConfigAnchor extends HTMLElement {
  connectedCallback() {
    const link = this.querySelector('a[href]');
    // `.go-config` est pose par <variant-selects> dans product-variant-picker :
    // il n'existe donc que la ou il y a vraiment des variantes a configurer.
    const section = document.querySelector('.go-config')?.closest('.shopify-section');
    if (!link || !section?.id) return;

    link.setAttribute('href', `#${section.id}`);

    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    // Un clic modificateur ouvre un onglet : intention de navigation, on laisse
    // le lien faire. Meme regle que <go-configurator-link>.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    const link = event.target.closest('a[href^="#"]');
    if (!link || !this.contains(link)) return;

    const section = document.getElementById(link.getAttribute('href').slice(1));
    if (!section) return;

    event.preventDefault();

    // Le defilement anime vit dans `go-smooth-scroll.js` — voir l'en-tete de ce
    // fichier pour la raison de ne pas confier le mouvement au navigateur. Le
    // repli couvre le cas d'une section qui aurait oublie de charger l'aide.
    const destination = section.getBoundingClientRect().top + window.scrollY;
    if (window.goSmoothScrollTo) {
      window.goSmoothScrollTo(destination);
    } else {
      section.scrollIntoView({ block: 'start' });
    }

    // Le saut natif ecrit le fragment dans l'URL ; en le remplacant par un
    // defilement anime, c'est a nous de le faire, sinon le lien devient
    // impartageable et le bouton « precedent » perd une etape.
    history.pushState(null, '', link.getAttribute('href'));
  }
}

// Le hero n'apparait qu'une fois par page, mais la garde reste la convention du
// theme : deux balises <script src> identiques s'executent deux fois, et la
// seconde definition d'un nom deja pris leve une exception.
if (!customElements.get('go-config-anchor')) {
  customElements.define('go-config-anchor', GoConfigAnchor);
}
